/**
 * Style Genome — design-language vocabulary (frozen).
 *
 * The controlled set of descriptive values every genome dimension draws from.
 * Genomes reference this vocabulary rather than repeating raw literals, so
 * there are no duplicated values and no hard-coded decisions scattered across
 * the registry. The vocabulary is additive: growing a list never breaks an
 * existing genome.
 *
 * NOTHING here is a pixel, a coordinate, or an editor concept — these are
 * pure design-language descriptors.
 */

export const FAMILIES = Object.freeze([
  'minimal', 'expressive', 'luxe', 'editorial', 'technical', 'retro', 'organic', 'playful', 'corporate',
]);

export const VISUAL_PHILOSOPHY = Object.freeze([
  'restraint', 'maximalism', 'clarity', 'opulence', 'honesty', 'experimentation',
  'warmth', 'precision', 'nostalgia', 'futurism', 'craft',
]);

export const COMPOSITION = Object.freeze([
  'symmetric', 'asymmetric', 'modular-grid', 'editorial-columns', 'centered-axis',
  'diagonal', 'layered-overlap', 'freeform', 'bento-grid',
]);

export const LAYOUT_PHILOSOPHY = Object.freeze([
  'content-first', 'image-first', 'type-first', 'whitespace-led', 'density-led', 'grid-led', 'story-led',
]);

export const GRID = Object.freeze([
  'strict-columns', 'loose-columns', 'modular', 'baseline-locked', 'broken-grid', 'asymmetric-grid', 'bento',
]);

export const RHYTHM = Object.freeze(['calm', 'steady', 'dynamic', 'staccato', 'flowing', 'punchy']);

export const SPACING = Object.freeze(['airy', 'comfortable', 'compact', 'dense', 'generous']);

export const TYPOGRAPHY = Object.freeze([
  'grotesque-solo', 'serif-display-sans-body', 'humanist-sans', 'geometric-sans',
  'editorial-serif', 'expressive-display', 'mono-accent', 'handwritten-accent', 'condensed-bold',
]);

export const COLOR = Object.freeze([
  'muted-neutral-accent', 'monochrome', 'high-contrast', 'duotone', 'pastel',
  'jewel-tones', 'earthy', 'neon', 'gradient-led', 'brand-forward',
]);

export const SHAPE = Object.freeze(['sharp-rect', 'soft-rect', 'pill', 'organic-blob', 'geometric', 'angular', 'mixed']);

export const DECORATION = Object.freeze([
  'none', 'sparse', 'organic-accents', 'geometric-accents', 'pattern-rich', 'collage', 'line-motifs',
]);

export const PHOTOGRAPHY = Object.freeze([
  'none', 'studio-clean', 'lifestyle', 'editorial', 'duotone-treated', 'moody', 'bright-airy', 'documentary',
]);

export const ILLUSTRATION = Object.freeze([
  'none', 'flat-vector', 'line-art', '3d', 'isometric', 'hand-drawn', 'abstract-shapes',
]);

export const ICON = Object.freeze(['none', 'thin-line', 'solid', 'duotone', 'rounded', 'sharp']);

export const EFFECTS = Object.freeze([
  'flat', 'subtle-shadow', 'soft-elevation', 'glassmorphism', 'neumorphism',
  'glow', 'grain', 'gradient-overlay', 'hard-shadow',
]);

export const CTA = Object.freeze(['prominent-solid', 'pill-accent', 'outline', 'text-link', 'oversized', 'floating']);

export const HERO = Object.freeze(['image-dominant', 'type-dominant', 'split', 'minimal', 'full-bleed', 'no-hero']);

export const CARD = Object.freeze(['flat-outline', 'elevated', 'glass', 'filled', 'borderless', 'bento-tile']);

export const BORDER = Object.freeze(['none', 'hairline', 'bold', 'dashed', 'framed']);

export const BACKGROUND = Object.freeze([
  'solid', 'subtle-gradient', 'mesh-gradient', 'pattern', 'texture', 'image-overlay', 'duotone',
]);

export const PREMIUM_CUES = Object.freeze([
  'restraint', 'precision', 'material-honesty', 'generous-whitespace', 'refined-type',
  'subtle-depth', 'considered-color', 'crafted-detail', 'confident-scale', 'tactile',
]);

export const AVOID = Object.freeze([
  'clutter', 'low-contrast', 'default-fonts', 'clip-art', 'heavy-gradient-on-text',
  'centered-everything', 'rainbow-palette', 'tiny-cta', 'stock-cliches', 'busy-background',
]);

/** Component families a genome prefers (Design Library owns the catalog). */
export const COMPONENT_FAMILIES = Object.freeze([
  'hero', 'pricing', 'features', 'testimonial', 'stats', 'gallery',
  'cta', 'header', 'footer', 'contact', 'timeline', 'menu', 'logo',
]);

/** Design-Library pattern tags a genome prefers. */
export const LIBRARY_PATTERNS = Object.freeze([
  'split-hero', 'centered-poster', 'editorial-columns', 'bento-grid', 'card-stack',
  'feature-row', 'image-band', 'minimal-stack', 'magazine-spread', 'gradient-hero',
  'collage-wall', 'stat-row',
]);

/** Compact distinguishing traits for the Design Memory Engine. */
export const DIVERSITY_TAGS = Object.freeze([
  'minimal', 'maximal', 'symmetric', 'asymmetric', 'image-led', 'type-led', 'geometric',
  'organic', 'flat', 'depth', 'mono', 'vibrant', 'editorial', 'playful', 'luxe',
  'technical', 'retro', 'soft', 'sharp', 'bento', 'gradient',
]);

// ── Expansion vocabulary (semantic seeds — still no pixels) ───────────────────

export const TYPE_SCALE = Object.freeze(['restrained', 'balanced', 'dramatic']);
export const RADIUS_TAG = Object.freeze(['none', 'subtle', 'medium', 'round', 'pill']);
export const ELEVATION_TAG = Object.freeze(['flat', 'soft', 'pronounced']);
